<template>
  <div>
    <!-- PC端对话框 -->
    <el-dialog
      title="编辑个人信息"
      :visible.sync="dialogVisible"
      width="500px"
      :close-on-click-modal="false"
      append-to-body
      custom-class="user-profile-dialog"
      @close="handleClose">
      <el-form
        ref="profileForm"
        :model="profileForm"
        :rules="profileFormRules"
        label-width="100px">
        <el-form-item label="用户名">
          <el-input v-model="profileForm.username" disabled></el-input>
        </el-form-item>
        <el-form-item label="姓名" prop="name">
          <el-input v-model="profileForm.name" ref="nameInput"></el-input>
        </el-form-item>
        <el-form-item label="性别" prop="sex">
          <el-radio-group v-model="profileForm.sex">
            <el-radio label="1">男</el-radio>
            <el-radio label="2">女</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="生日" prop="birthday">
          <el-date-picker
            v-model="profileForm.birthday"
            type="date"
            placeholder="选择日期"
            value-format="yyyy-MM-dd">
          </el-date-picker>
        </el-form-item>
        <el-form-item label="新密码" prop="password">
          <el-input 
            v-model="profileForm.password" 
            type="password" 
            placeholder="留空表示不修改密码"
            show-password>
          </el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="handleClose">取 消</el-button>
        <el-button type="primary" @click="submitProfile" :loading="loading">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 移动端对话框 -->
    <el-dialog
      title="编辑个人信息"
      :visible.sync="mobileDialogVisible"
      width="90%"
      center
      :close-on-click-modal="false"
      append-to-body
      custom-class="user-profile-dialog mobile"
      @close="handleClose">
      <el-form
        ref="mobileProfileForm"
        :model="profileForm"
        :rules="profileFormRules"
        label-width="80px">
        <el-form-item label="用户名">
          <el-input v-model="profileForm.username" disabled></el-input>
        </el-form-item>
        <el-form-item label="姓名" prop="name">
          <el-input v-model="profileForm.name" ref="mobileNameInput"></el-input>
        </el-form-item>
        <el-form-item label="性别" prop="sex">
          <el-radio-group v-model="profileForm.sex">
            <el-radio label="1">男</el-radio>
            <el-radio label="2">女</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="生日" prop="birthday">
          <el-date-picker
            v-model="profileForm.birthday"
            type="date"
            placeholder="选择日期"
            value-format="yyyy-MM-dd"
            style="width: 100%">
          </el-date-picker>
        </el-form-item>
        <el-form-item label="新密码" prop="password">
          <el-input 
            v-model="profileForm.password" 
            type="password" 
            placeholder="留空表示不修改密码"
            show-password>
          </el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="handleClose">取 消</el-button>
        <el-button type="primary" @click="submitProfile" :loading="loading">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import { isMobile } from '@/utils/device'

export default {
  name: 'UserProfileDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    userInfo: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      profileForm: {
        id: null,
        username: '',
        name: '',
        sex: '1',
        birthday: '',
        password: ''
      },
      profileFormRules: {
        name: [
          { required: true, message: '请输入姓名', trigger: 'blur' }
        ],
        sex: [
          { required: true, message: '请选择性别', trigger: 'change' }
        ],
        birthday: [
          { required: true, message: '请选择生日', trigger: 'change' }
        ],
        password: [
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible && !isMobile()
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    mobileDialogVisible: {
      get() {
        return this.visible && isMobile()
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initForm()
      }
    },
    userInfo: {
      handler(newVal) {
        if (newVal && this.visible) {
          this.initForm()
        }
      },
      deep: true
    }
  },
  methods: {
    initForm() {
      // 初始化表单数据
      this.profileForm = {
        id: this.userInfo.id || null,
        username: this.userInfo.username || '',
        name: this.userInfo.name || '',
        sex: this.userInfo.sex || '1',
        birthday: this.userInfo.birthday || '',
        password: ''
      }
      
      // 清除验证状态
      this.$nextTick(() => {
        if (this.$refs.profileForm) {
          this.$refs.profileForm.clearValidate()
        }
        if (this.$refs.mobileProfileForm) {
          this.$refs.mobileProfileForm.clearValidate()
        }
      })
    },
    
    async submitProfile() {
      const formRef = isMobile() ? this.$refs.mobileProfileForm : this.$refs.profileForm
      
      formRef.validate(async valid => {
        if (valid) {
          this.loading = true
          try {
            const submitData = {
              name: this.profileForm.name,
              sex: this.profileForm.sex,
              birthday: this.profileForm.birthday
            }
            
            // 如果填写了密码，则包含在提交数据中
            if (this.profileForm.password && this.profileForm.password.trim()) {
              submitData.password = this.profileForm.password
            }
            
            const response = await request({
              url: '/api/user/profile',
              method: 'put',
              data: submitData
            })
            
            if (response.code === 200) {
              this.$message.success('更新个人信息成功')
              
              // 更新本地存储的用户信息
              const userInfo = localStorage.getItem('userInfo')
              if (userInfo) {
                const userData = JSON.parse(userInfo)
                userData.name = this.profileForm.name
                localStorage.setItem('userInfo', JSON.stringify(userData))
              }
              
              // 通知父组件更新用户信息
              this.$emit('profile-updated', {
                name: this.profileForm.name,
                sex: this.profileForm.sex,
                birthday: this.profileForm.birthday
              })
              
              this.handleClose()
            } else {
              this.$message.error(response.message || '更新个人信息失败')
            }
          } catch (error) {
            console.error('更新个人信息失败:', error)
            this.$message.error(error.message || '更新个人信息失败')
          } finally {
            this.loading = false
          }
        }
      })
    },
    
    handleClose() {
      this.$emit('update:visible', false)
      // 重置表单
      this.profileForm.password = ''
    }
  }
}
</script>

<style scoped>
.dialog-footer {
  text-align: center;
}
</style>

<style>
/* 用户信息编辑对话框样式 */
.user-profile-dialog {
  margin-top: 15vh !important;
}

.user-profile-dialog .el-dialog {
  margin-top: 15vh !important;
}

/* 移动端样式优化 */
.user-profile-dialog.mobile {
  margin-top: 10vh !important;
}

.user-profile-dialog.mobile .el-dialog {
  margin-top: 10vh !important;
  margin: 10vh auto 0 !important;
  height: 80vh;
  display: flex;
  flex-direction: column;
}

.user-profile-dialog.mobile .el-dialog__body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.user-profile-dialog.mobile .el-form-item__label {
  font-size: 14px;
}

.user-profile-dialog.mobile .el-input__inner,
.user-profile-dialog.mobile .el-textarea__inner {
  font-size: 14px;
}

/* 强制设置对话框包装器样式 - 不贴顶显示 */
body .el-dialog__wrapper {
  padding-top: 10vh !important;
}

/* 移动端响应式调整 */
@media (max-width: 768px) {
  body .el-dialog__wrapper {
    padding-top: 5vh !important;
  }
}

@media (max-height: 600px) {
  body .el-dialog__wrapper {
    padding-top: 3vh !important;
  }
  
  .user-profile-dialog.mobile {
    height: 85vh;
  }
}
</style>